import { client } from '../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../queries/widgets';
import { normalizeAutomatedColumnsInput } from '../../../../../utils/normalizeAutomatedColumnsInput';
import { isSquadDynamicCell } from './squadFondsdatenDynamic';
import { formatSquadGebuehr, SQUAD_DYNAMIC_GEBUEHREN_KEYS } from './squadGebuehrenDynamic';
import { formatSquadUntil } from './squadSrriwert';

/**
 * Fetch the dynamic Gebühren rows for the fund ISIN as of the factsheet date. Every table
 * field of a row is asked for separately, so a missing TER date cannot leave a dangling
 * "Stand:" behind. Returns the finished wording keyed by row.
 */
export async function fetchSquadGebuehrenValues(
  weitereDaten: Record<string, unknown> | undefined | null,
  isin: string,
  until?: string | null,
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const fundIsin = String(isin || '').trim().toUpperCase();
  if (!fundIsin || !weitereDaten) return out;

  const requests: Array<{ key: (typeof SQUAD_DYNAMIC_GEBUEHREN_KEYS)[number]; part: number; field: any }> = [];
  SQUAD_DYNAMIC_GEBUEHREN_KEYS.forEach((key) => {
    const cell = weitereDaten[key];
    if (!isSquadDynamicCell(cell)) return;
    cell.fields
      .filter((f) => f.type === 'table')
      .forEach((field, part) => requests.push({ key, part, field }));
  });
  if (!requests.length) return out;

  try {
    const res = await client.query({
      query: GET_AUTOMATED_COLUMNS,
      variables: {
        param: {
          data: normalizeAutomatedColumnsInput([requests.map((r) => ({ isDynamic: true, fields: [r.field] }))]),
          isin: fundIsin,
          until: formatSquadUntil(until) || undefined,
        },
      },
      fetchPolicy: 'network-only',
    });
    const row = res?.data?.getAutomatedColumns?.[0];
    if (!Array.isArray(row)) return out;
    const raws = new Map<string, Array<string | undefined>>();
    requests.forEach((r, i) => {
      const parts = raws.get(r.key) || [];
      parts[r.part] = row[i] == null ? undefined : String(row[i]);
      raws.set(r.key, parts);
    });
    raws.forEach((parts, key) => {
      const text = formatSquadGebuehr(key as (typeof SQUAD_DYNAMIC_GEBUEHREN_KEYS)[number], parts);
      if (text) out.set(key, text);
    });
  } catch {
    // rows stay empty; the PDF dialog lists them as missing
  }
  return out;
}
